import { useEffect, useState } from 'preact/hooks';

// A hand-rolled, dependency-free router rather than pulling in
// preact-router: hash-based paths (`#/auditions/callback`) work on ANY
// static host with zero server-side rewrite config — which mattered
// because A2.1a shipped no server wiring at all; real path-based routing
// can follow once something owns an SPA-fallback rewrite rule.
//
// This stays a raw string in/string out router on purpose — routes/
// route.ts's parseRoute/routePath own all the STRUCTURE (the 5 tabs, each
// one's Strip sub-view, the Materials workshop) on top of the plain path
// string this hook hands back; this file only ever talks to
// `window.location.hash`.
function normalize(hash: string): string {
  const path = hash.replace(/^#/, '');
  return path || '/';
}

export function useHashRoute(): [string, (path: string, opts?: { replace?: boolean }) => void] {
  const [route, setRoute] = useState(() => normalize(window.location.hash));

  useEffect(() => {
    const onChange = () => setRoute(normalize(window.location.hash));
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);

  // CV3: `replace` swaps the CURRENT history entry instead of pushing one.
  // Exists for route NORMALIZATION (the bare `jobs` tab canonicalizing to
  // the job it resolved): a PUSHED entry there would turn the back button
  // into a loop (back to /jobs -> it re-resolves -> forward again).
  // history.replaceState + an explicit hashchange dispatch, deliberately:
  // replaceState never fires hashchange on its own (per spec), and
  // location.replace('#…') fires it in real browsers but NOT in happy-dom —
  // this pair behaves identically everywhere, and the listener above reads
  // location.hash rather than event fields, so a bare Event suffices.
  const navigate = (path: string, opts?: { replace?: boolean }) => {
    if (opts?.replace) {
      window.history.replaceState(window.history.state, '', `#${path}`);
      window.dispatchEvent(new Event('hashchange'));
      return;
    }
    window.location.hash = path;
  };

  return [route, navigate];
}
